import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { api, imgSrc } from "../lib/api";
import { BotanicalDivider } from "../components/Botanical";

const ease = [0.22, 1, 0.36, 1];

const spanFor = (i) => {
  const m = i % 7;
  if (m === 0) return "col-span-2 row-span-2";
  if (m === 3) return "col-span-2";
  if (m === 5) return "row-span-2";
  return "";
};

const Gallery = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/gallery").then((res) => setItems(res.data)).catch(() => {});
  }, []);

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(items.map((i) => i.category)))],
    [items]
  );
  const visible = filter === "All" ? items : items.filter((i) => i.category === filter);

  return (
    <section id="gallery" className="relative py-24 md:py-32" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease }}
          className="text-center mb-14"
        >
          <p className="text-xs uppercase tracking-[0.3em] font-semibold text-[#8B5D6C] mb-4">The Gallery of Bakes</p>
          <h2 className="font-serif text-4xl md:text-5xl text-[#4A2834]">Recent work, fresh from the studio</h2>
          <BotanicalDivider className="w-64 mx-auto mt-6 text-[#C5A059]" />
        </motion.div>

        <div className="flex flex-wrap justify-center gap-3 mb-12" data-testid="gallery-filters">
          {categories.map((cat) => (
            <button
              key={cat}
              data-testid={`gallery-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setFilter(cat)}
              className={`px-5 py-2 rounded-full text-sm tracking-wide transition-all duration-300 border ${
                filter === cat
                  ? "bg-[#8B5D6C] text-[#F9F6F0] border-[#C5A059]/60"
                  : "border-[#8B5D6C]/25 text-[#70505D] hover:border-[#C5A059] hover:text-[#8B5D6C]"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <motion.div layout className="grid grid-cols-2 md:grid-cols-4 auto-rows-[170px] md:auto-rows-[210px] gap-3 md:gap-4" data-testid="gallery-grid">
          <AnimatePresence mode="popLayout">
            {visible.map((item, i) => (
              <motion.button
                layout
                key={item.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, delay: (i % 4) * 0.08, ease }}
                onClick={() => setActive(item)}
                data-testid={`gallery-item-${item.id}`}
                className={`group relative overflow-hidden rounded-2xl text-left ${spanFor(i)}`}
              >
                <img
                  src={imgSrc(item)}
                  alt={item.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2E1E26]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-0 inset-x-0 p-5 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#E8D8B6]">{item.category}</p>
                  <p className="font-serif text-xl text-[#F9F6F0] mt-1">{item.title}</p>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-[#2E1E26]/85 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
            onClick={() => setActive(null)}
            data-testid="gallery-lightbox"
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              transition={{ duration: 0.45, ease }}
              className="bg-[#F9F6F0] rounded-3xl overflow-hidden max-w-4xl w-full grid md:grid-cols-2 max-h-[85vh]"
              onClick={(e) => e.stopPropagation()}
              data-testid="gallery-lightbox-card"
            >
              <div className="relative h-64 md:h-auto">
                <img src={imgSrc(active)} alt={active.title} className="absolute inset-0 w-full h-full object-cover" />
              </div>
              <div className="p-8 md:p-10 flex flex-col">
                <p className="text-xs uppercase tracking-[0.3em] font-semibold text-[#C5A059]">{active.category}</p>
                <h3 className="font-serif text-3xl md:text-4xl text-[#4A2834] mt-3">{active.title}</h3>
                <span className="gold-rule w-16 my-6" />
                <p className="text-[#70505D] leading-relaxed text-sm md:text-base">{active.description}</p>
                <button
                  onClick={() => setActive(null)}
                  data-testid="gallery-lightbox-close"
                  className="mt-auto pt-8 self-start inline-flex items-center gap-2 text-sm text-[#8B5D6C] border-b border-[#C5A059] pb-1 hover:text-[#4A2834] transition-colors"
                >
                  <X size={14} /> Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
